/**
 * QR Auth API Client
 * Task 5: フロントエンドの API クライアントを実装する
 * Requirements: 2.1, 3.6, 4.1
 *
 * QR ログイントークンの発行・失効・期限切れ一括削除を行う認証付きクライアント。
 * 既存の userManagementApi と同一の createAuthenticatedClient パターンに準拠する。
 *
 * Task 7.2: 公開（NONE 認可）の `/auth/qr-login` 消費クライアントを追加。
 *       Cognito トークンを付与しないプレーンな axios クライアントを用いる
 *       （未認証端末からの消費要求であり、認証ヘッダは付けない）。
 */
import axios from 'axios';
import { createApiClient, createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * 全ユーザー一括発行（`POST /admin/qr-tokens/bulk`）の 1 ユーザー分の結果。
 * PDF 組版用にユーザー情報を含む。生トークンは deepLink 内にのみ含まれる。
 */
export interface BulkIssueTokenItem {
  userId: string;
  username: string;
  name: string;
  email: string;
  companyName: string; // 会社名（PDF表示用）
  departmentName: string; // 主部署名（PDF表示用）
  deepLink: string; // 例: https://<app>/qr-login?t=<生トークン>
  expiresAt: string; // ISO8601
}

export interface BulkIssueTokensResponse {
  items: BulkIssueTokenItem[];
  issuedCount: number;
}

/**
 * 一括発行の対象ユーザー範囲（権限による絞り込み）。
 * - 'all'   : 全ユーザー（既定）
 * - 'user'  : 一般ユーザーのみ
 * - 'admin' : 管理者のみ
 */
export type QrIssueTarget = 'all' | 'user' | 'admin';

export interface RevokeAllTokensResponse {
  revokedCount: number;
}

export interface CleanupExpiredResponse {
  deletedCount: number;
}

/**
 * QR ログイン消費（`POST /auth/qr-login`）の成功レスポンス。
 * - 本番: `{ username, challengeSecret }`（フロントが Custom Auth を実行）。
 * - ローカル: `{ mock: true, tokens }`（mock-JWT を直接適用 / 決定 D5）。
 * フロントはこのレスポンス形態（mock の有無）で分岐し、自身の環境推測には依存しない。
 */
export type QrLoginConsumeResponse =
  | { username: string; challengeSecret: string }
  | {
      mock: true;
      tokens: { idToken: string; accessToken: string; refreshToken: string };
    };

/**
 * QR ログイン消費の失敗理由（Requirement 5.7）。
 * 生トークン・ハッシュは一切含まれず、UX 表示用の理由のみが返る。
 */
export type QrLoginReason = 'invalid' | 'used' | 'expired';

export interface QrLoginErrorBody {
  error?: string;
  reason?: QrLoginReason;
}

/**
 * 未認証の QR ログイン消費クライアント（NONE 認可）。
 * Cognito トークンを付与しないプレーンな axios インスタンスを使用する。
 */
let publicApiClient: ReturnType<typeof createApiClient> | null = null;

const getPublicApiClient = () => {
  if (!publicApiClient) {
    publicApiClient = createApiClient({ baseURL: API_BASE_URL });
  }
  return publicApiClient;
};

/**
 * 生トークンを消費して QR ログインを開始する（未認証 / Requirement 4.1, 5.7）。
 * POST /auth/qr-login  body { token }
 *
 * 成功時はレスポンスボディ（本番/ローカルいずれか）をそのまま返す。
 * 失敗時は axios エラーをそのまま送出し、呼び出し側が status / reason を解釈する。
 */
export async function consumeQrLogin(token: string): Promise<QrLoginConsumeResponse> {
  const client = getPublicApiClient();
  const response = await client.post<QrLoginConsumeResponse>('/auth/qr-login', { token });
  return response.data;
}

/**
 * axios エラーから QR ログインの失敗理由を抽出するヘルパー。
 * サーバーが付与した `reason`（invalid/used/expired）を優先し、無い場合は
 * HTTP ステータス（410=expired, 401=invalid 既定）から推定する。
 */
export function resolveQrLoginReason(error: unknown): QrLoginReason {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as QrLoginErrorBody | undefined;
    if (body?.reason) {
      return body.reason;
    }
    if (error.response?.status === 410) {
      return 'expired';
    }
  }
  return 'invalid';
}

/**
 * 一括発行の絞り込み条件。target(権限) / companyId / userId は AND で絞り込む。
 * - 指定なし        : 全ユーザー（全社横断）
 * - companyId のみ  : その会社の全員
 * - userId のみ     : その 1 名のみ
 */
export interface IssueTokensBulkParams {
  target?: QrIssueTarget;
  companyId?: string;
  userId?: string;
}

/**
 * 対象ユーザー分のワンタイムログイントークンを一括発行する（管理者のみ）
 * POST /admin/qr-tokens/bulk  body { target?, companyId?, userId? }
 * 各ユーザーの deepLink・有効期限とユーザー情報（PDF 組版用）を返す。
 * 後方互換: 文字列（target）を渡した場合は従来どおり権限絞り込みとして解釈する。
 */
export async function issueTokensBulk(
  params: IssueTokensBulkParams | QrIssueTarget = {}
): Promise<BulkIssueTokensResponse> {
  const client = getApiClient();
  const p: IssueTokensBulkParams = typeof params === 'string' ? { target: params } : params;
  const body: Record<string, unknown> = {};
  if (p.target) body.target = p.target;
  if (p.companyId) body.companyId = p.companyId;
  if (p.userId) body.userId = p.userId;
  const response = await client.post<BulkIssueTokensResponse>('/admin/qr-tokens/bulk', body);
  return response.data;
}

/**
 * 有効な全トークンを一括強制失効させる（管理者のみ）
 * DELETE /admin/qr-tokens
 * 現在有効（未使用・期限内）な全ユーザー分のトークンを一斉に無効化する。
 */
export async function revokeAllActiveTokens(): Promise<RevokeAllTokensResponse> {
  const client = getApiClient();
  const response = await client.delete<RevokeAllTokensResponse>('/admin/qr-tokens');
  return response.data;
}

/**
 * 期限切れトークンを一括削除する（管理者のみ・任意）
 * DELETE /admin/qr-tokens/expired
 */
export async function cleanupExpired(): Promise<CleanupExpiredResponse> {
  const client = getApiClient();
  const response = await client.delete<CleanupExpiredResponse>('/admin/qr-tokens/expired');
  return response.data;
}
